import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom';
import {AnyReportedParameters, base64ToUnicode, isAwareDevice} from "../../../helpers/helperfunctions.js";
import {useDispatch, useSelector} from "react-redux";
import NavTopBar from "../../../components/shared/device/NavTopBar";
import {useTranslation} from "react-i18next";
import {
    saveConfigurationByDeviceList,
    saveConfigurationByGroupList
} from "../../../helpers/reduxstore/reducers/groupReducer.js";
import SaveForGroupsModal from "../../../components/settings/saveforgroupsmodal/SaveForGroupsModal.js";
import InformationModal from "../../../components/shared/informationmodal/InformationModal.js";
import {DeviceParameterContent} from "../../../components/parameters/DeviceParameterContent.js";
import {
    findNodeInHierarchyById,
    initTechnicalParamsState,
    updateTechnicalParamsState
} from "../../../helpers/reduxstore/reducers/technicalParamsReducer.js";
import useInterval from "../../../helpers/hooks/useInterval.js";
import useNavigationGuardByRole from "../../../helpers/hooks/useNavigationGuardByRole.js";
import ModalTopText from '../../../components/shared/modaltoptext/ModalTopText'
import {allowedPageRoles} from "../../../helpers/constants.js";
import {loadCurrentDevice} from "../../../helpers/reduxstore/reducers/deviceReducer.js";
import useSetLocation from "../../../helpers/hooks/useSetLocation.js";
import {isEqual} from "lodash";
import {loadCurrentDeviceWrapper} from "../../../helpers/deviceHelper.js";
import {IfDeviceFound} from "../../../components/dashboard/IfDeviceFound.js";
import {getFirmwareFileForVersion} from "../../../api/api.js";
import {LoadSpinner} from "../../../components/shared/loader/LoadSpinner.js";
import {ParametersTreeView} from "./ParametersTreeView.js";
import {DevicePagesTopInformation} from "../technical/DevicePagesTopInformation.js";
import {getDeviceTypesFromGroups} from "../../../api/api"
import Select from "react-select";
import {XMLParser} from "fast-xml-parser";


export default function DeviceParametersPage() {
    useSetLocation()
    useNavigationGuardByRole(allowedPageRoles.DeviceParametersPage)

    const [showLoadSpinner, setShowLoadSpinner] = useState(true)
    const [fileLoadErrorHtml, setFileLoadErrorHtml] = useState("")
    const [warningText, setWarningText] = useState("")
    const [oldFirmwareFileVersion, setOldFirmwareFileVersion] = useState("")

    const {t} = useTranslation(['parameterspage', 'common', 'settingspage'])
    const dispatch = useDispatch()
    const {deviceSerialNumber} = useParams();

    const {hierarchyList, paramSpec, selectedParameterId} = useSelector((state) => state.technicalParams)
    const {currentDevice: device} = useSelector(g => g.device)
    const [selectedParam, setSelectedParam] = useState(null)
    const isAware = isAwareDevice(deviceSerialNumber)

    useEffect(() => {
        let fallBackVersion = isAware ? "1.4.36" : "6263"
        let newFirmwareVersion = isAware ? device?.device_twin?.reported?.applicationVersion : device?.device_twin?.reported?.applicationVersion?.replace(".","");
        // TODO: find better solution than hardcoding if no version
        if (!newFirmwareVersion) {
            setWarningText(`Device has no firmware version so used version '${fallBackVersion}'. Ask Kenneth or Martin for additional information or for changing fallback version.`)
        }

        const loadHierarchyFromFileContent = (fileContent) => {
            const decodedFileContent = base64ToUnicode(fileContent)
            const initialHierarchy = isAware ? generateInitialHierarchyFromFirmwareParameterFile(decodedFileContent) : generateInitialHierarchyFromFirmwareParameterFileJE(decodedFileContent)
            dispatch(initTechnicalParamsState(initialHierarchy))
            dispatch(updateTechnicalParamsState({device: device, t}))
        }

        if (newFirmwareVersion && oldFirmwareFileVersion !== newFirmwareVersion) {
            setOldFirmwareFileVersion(newFirmwareVersion)
            getFirmwareFileForVersion(newFirmwareVersion)
                .then(res => {
                    setFileLoadErrorHtml("")
                    loadHierarchyFromFileContent(res.fileContent)
                })
                .catch(err => {
                    console.error(err)
                    if (newFirmwareVersion === fallBackVersion) {
                        setFileLoadErrorHtml(`Tried to download firmware file for version '${newFirmwareVersion}', but it could not be found in Azure blobstorage.<br/><br/>Verify that the file is set in the <a href="/firmware/manage">firmware management page</a>. Include it if not.`)
                        return
                    }
                    return getFirmwareFileForVersion(fallBackVersion)
                        .then(res => {
                            setFileLoadErrorHtml("")
                            setWarningText(`Could not find firmware file for version '${newFirmwareVersion}', so used fallback version '${fallBackVersion}' instead.`)
                            loadHierarchyFromFileContent(res.fileContent)
                        })
                        .catch(fallbackErr => {
                            console.error(fallbackErr)
                            setFileLoadErrorHtml(`Tried to download firmware file for version '${newFirmwareVersion}' and fallback version '${fallBackVersion}', but neither could be found in Azure blobstorage.<br/><br/>Verify that the file is set in the <a href="/firmware/manage">firmware management page</a>. Include it if not.`)
                        })
                })
                .finally(() => {
                    setShowLoadSpinner(false)
                })
        } else {
            dispatch(updateTechnicalParamsState({device: device, t}))
        }
    }, [device])

    useEffect(() => {
        let foundNode = findNodeInHierarchyById(hierarchyList, selectedParameterId)?.data;
        // only update selected parameter if anything new is found, so value is not reset every 5 seconds
        if (!isEqual(foundNode, selectedParam) || selectedParam == null) {
            setSelectedParam(foundNode)
        }
    }, [device, hierarchyList, selectedParameterId])

    useInterval(() => {
        loadCurrentDeviceWrapper(device, device.serial_number, dispatch)
    }, 5000)

    const [stagedConfiguration, setStagedConfiguration] = useState({})
    const [showGroupModal, setShowGroupModal] = useState(false)
    const [showGroupWarningModal, setShowGroupWarningModal] = useState(false)
    const [showConfirmation, setShowConfirmation] = useState(false)
    const [selectedDeviceIds, setSelectedDeviceIds] = useState([])
    const [selectedGroupIds, setSelectedGroupIds] = useState([])
    const [selectedDeviceTypes, setSelectedDeviceTypes] = useState([])
    const [deviceTypeList, setDeviceTypeList] = useState([])
    

    const promptForSave = (settings, saveForGroup) => {
        setStagedConfiguration(settings)
        if (saveForGroup) {
            setSelectedDeviceIds([])
            setSelectedGroupIds([])
            setShowGroupModal(true)
        } else setShowConfirmation(true)
    }
    const cancelConfiguration = () => {
        setSelectedDeviceIds([device.id])
        setStagedConfiguration({})
        setShowGroupModal(false)
        setShowConfirmation(false)
    }

    const saveForGroupValidation = async () => {
        if (selectedGroupIds.length === 0) {
            commitChanges()
            return;
        }
        const result = await getDeviceTypesFromGroups(selectedGroupIds, !isAware);
        if(result !== null && result.deviceTypes.length > 1) {
            const options = [
                ...result.deviceTypes
                    .slice() // create a copy
                    .sort() // sort alphabetically
                    .map(type => ({ value: type, label: type }))
            ];
            setDeviceTypeList(options);
            setShowGroupWarningModal(true);
        }else {
            commitChanges();
        }
    }
    
    const cancelShowGroupWarningModal = () => {
        setShowGroupWarningModal(false)
    }

    const acceptShowGroupWarningModal = () => {
        setShowGroupWarningModal(false)
        commitChanges()
    }

    const onChangeDeviceType = (option) => {
        setSelectedDeviceTypes(option);
    };


    const commitChanges = async () => {
        if (stagedConfiguration) {
            const deviceTypes = selectedDeviceTypes.length > 0 ? selectedDeviceTypes.map(dt => dt.value) : [];
            if (selectedDeviceIds.length === 0 && selectedGroupIds.length === 0) {
                await dispatch(saveConfigurationByDeviceList(stagedConfiguration, [device.id], deviceTypes, !isAwareDevice(device.serial_number)));
            } else if (selectedDeviceIds.length !== 0) {
                await dispatch(saveConfigurationByDeviceList(stagedConfiguration, selectedDeviceIds, deviceTypes, !isAwareDevice(device.serial_number)));
            } else {
                await dispatch(saveConfigurationByGroupList(stagedConfiguration, selectedGroupIds, deviceTypes, !isAwareDevice(device.serial_number)));
            }
            await dispatch(loadCurrentDevice(device.serial_number));
        }
        setShowGroupModal(false)
        setShowConfirmation(false)
    }

    // async function downloadFirmwareFile() {
    //     console.log("Downloading firmware file", oldFirmwareFileVersion)
    //     const res = await getFirmwareFile(oldFirmwareFileVersion)
    //     saveBase64String(oldFirmwareFileVersion, res.fileContent)
    // }

    if (!device.connected || !AnyReportedParameters(device)) return <div className='d-flex flex-column flex-grow-1'>
        <DevicePagesTopInformation device={device}/>
        <NavTopBar deviceSerialNumber={deviceSerialNumber} currentPage='parameters'/>
        <div className='d-flex flex-grow-1 bg-white rounded-3 p-3 mb-3'>
            {!device.connected ? t('settingspage:settings_not_available_device_offline') : t('settingspage:settings_not_available_no_parameters')}
        </div>
    </div>

    return (
        <IfDeviceFound>
            {/*=== Modals ===*/}
            <SaveForGroupsModal show={showGroupModal} dismiss={cancelConfiguration}
                                setSelectedGroupIds={setSelectedGroupIds}
                                accept={saveForGroupValidation}/>
            <InformationModal show={showConfirmation} title={t('settingspage:confirm_modal.title')}
                                body={t('settingspage:confirm_modal.body')}
                                dismiss={cancelConfiguration} accept={commitChanges}/>
        
            <InformationModal show={showGroupWarningModal} title={t('settingspage:save_group_multiple_device_types_modal.title')}
                    body={
                    <React.Fragment>
                        <ModalTopText body={t('settingspage:save_group_multiple_device_types_modal.body')}/>
                        <Select
                        name="deviceTypes"
                        placeholder={t('settingspage:save_group_multiple_device_types_modal.select')}
                        value={selectedDeviceTypes}
                        options={deviceTypeList}
                        onChange={onChangeDeviceType}
                        isMulti
                        />
                    </React.Fragment>
                    }
                    dismiss={cancelShowGroupWarningModal} accept={acceptShowGroupWarningModal}
            />  

            <div className='d-flex flex-column flex-grow-1'>
                <DevicePagesTopInformation device={device}/>
                <NavTopBar deviceSerialNumber={deviceSerialNumber} currentPage='parameters'/>

                {showLoadSpinner &&
                    <LoadSpinner style={{marginTop: "10vh"}} height='60px' width='60px'/>
                }
                {!showLoadSpinner &&
                    <>
                        {warningText && <p className={'p-2'}>{warningText}</p>}
                        {!fileLoadErrorHtml &&
                            <div className="d-block d-md-flex">

                                <ParametersTreeView hierarchyList={hierarchyList}
                                                    selectedParameterId={selectedParameterId}
                                                    showId={true}/>

                                <DeviceParameterContent className='d-flex flex-column h-100'
                                                        selectedParameter={selectedParam}
                                                        promptForSave={promptForSave} isAware={true}
                                                        dynamicParamSpec={paramSpec}/>

                            </div>
                        }
                        {fileLoadErrorHtml &&
                            <p className={'p-2'} dangerouslySetInnerHTML={{__html: fileLoadErrorHtml}}/>}
                    </>
                }
            </div>
        </IfDeviceFound>
    )
}

function parseBitmaskOptions(bitmask) {
    let s = bitmask
    const optionList = []
    if (bitmask.startsWith("{")) s = s.substring(1, s.length - 1)
    if (bitmask.startsWith("\"{")) s = s.substring(2, s.length - 2)
    s.split(",").forEach(kv => {
        let [key, value] = kv.trim().split("=")
        let [valueName, valueNeg, valuePos] = value.substring(1, value.length - 1).split("|").map(v => v.trim())
        optionList.push({
            "name": valueName.toLowerCase().replaceAll(" ", "_").replaceAll("-", "_"),
            "name_orig": valueName,
            "pos": valuePos,
            "neg": valueNeg
        })
    })
    return optionList
}

function parseNameKey(name, id) {
    let s = name.toLowerCase()
        .replaceAll(" ", "_").replaceAll("-", "_")
        .replaceAll(",", "").replaceAll(".", "")
        .replaceAll(":", "").replaceAll("%", "percent")
        .replaceAll("(", "").replaceAll(")", "")
        .replaceAll("/", "").replaceAll("[", "")
        .replaceAll("]", "").replaceAll("?", "")
        .replaceAll("#", "")
    if (s.endsWith("_")) s = s.substring(0, s.length - 1)
    if (id >= 700 && id <= 820) s = "threshold_" + s
    return s
}

function parseOrigName(name, id) {
    let s = name
    if (id >= 700 && id <= 820) s = "Threshold " + s
    return s
}

function generateInitialHierarchyFromFirmwareParameterFileJE(fileContent) {
        const parser = new XMLParser();
        let parsedNodeList = parser.parse(fileContent).parameters.parameter;
    
        const parameterList = parsedNodeList.map(n => {
            const o = {
                id: n.ParNo,
                // param_no: n.ParNo,
                // parent: n.GuiGrouping,
                name: parseNameKey(n.ParName, n.ParNo),
                name_orig: parseOrigName(n.ParName, n.ParNo),
                desc: n.ParDescription,
                type: n.ParValType,
                min: n.ParMin,
                max: n.ParMax,
                res: n.ParResolution,
                default: n.ParDefault,
                showInTechnical: n.ServerShowInTechnicalMenu,
                unit: n.ParUnit,
                MenuPos: n.MenuPos, 
                scope: n.ParScope,
                guiGroupNameList: n.GuiGrouping ? n.GuiGrouping.split("\\") : [],
            }
            if (n.ParValType === "enum")
                o.options = Object.fromEntries(n.ParEnumList
                    .substring(1, n.ParEnumList.length - 1)
                    .split(",")
                    .map(s => s.trim().split("=").map(e => e.trim())))
            if (n.ParValType === "bitmask")
                o.options = parseBitmaskOptions(n.ParEnumList)
            return o
        })
        const parameterIdObjectMap = Object.fromEntries(parameterList.map(n => ([n.id, n])))
        const parameterIdMap = Object.fromEntries(parameterList.map(n => ([n.name, n.id])))
    
        const paramWithoutTelemList = parameterList.filter(p => p.id < 1000).sort((a, b) => a.MenuPos - b.MenuPos)
        const paramAllWithoutATelemList = parameterList.filter(p => p.id < 1000)
    
        // create hierarchy from GuiGrouping
        const guiGroupingHierarchyList = []
        for (const param of paramWithoutTelemList) {
            let currGroupList = guiGroupingHierarchyList
            for (const groupName of param.guiGroupNameList) {
                let foundNode = currGroupList.find(g => g.name === groupName)
                if (!foundNode) {
                    let newNode = {
                        name: groupName,
                        type: "category",
                        id: crypto.randomUUID(),
                        isCollapsed: true,
                        isVisibleToTechnicalBasic: true,
                        children: []
                    }
                    currGroupList.push(newNode)
                    currGroupList = newNode.children
                } else {
                    currGroupList = foundNode.children
                }
            }
            if (currGroupList !== guiGroupingHierarchyList) {
                currGroupList.push({
                    type: "parameter",
                    isVisibleToTechnicalBasic: param.showInTechnical, // TODO: how to see if should be visible
                    data: param
                })
            }
        }
    
        return {
            selectedParameter: null,
            paramSpec: parameterIdObjectMap,
            hierarchyList: [
                ...guiGroupingHierarchyList.sort((a, b) => a.MenuPos - b.MenuPos),
                {
                    name: "All Parameters",
                    type: "category",
                    id: "5fa389d5-dcc3-48f0-adc7-c4c15215a2f0",
                    isCollapsed: true,
                    isVisibleToTechnicalBasic: true,
                    children: paramAllWithoutATelemList.map(p => ({
                        type: "parameter",
                        isVisibleToTechnicalBasic: p.showInTechnical, // TODO: how to see if should be visible
                        data: p
                    }))
                }
            ]
        }
}

function generateInitialHierarchyFromFirmwareParameterFile(fileContent) {
    // map remote parameters to layout parameters to show
    const parsedRemoteParameterList = JSON.parse(fileContent)
    const parentNodeList = parsedRemoteParameterList.filter(o => o.type === "parent").map(o => ({
        name: o.text,
        type: "category",
        id: o.id,
        parent: o.parent,
        isCollapsed: true,
        isVisibleToTechnicalBasic: true,
        children: []
    }))
    const IdParentNodeMap = parentNodeList.map(o => ([o.id, o])).toMap()
    parentNodeList.forEach(o => { // Glue category nodes together
        const parentNode = IdParentNodeMap[o.parent]
        if (parentNode) parentNode.children.push(o)
    })

    const parameterNodeList = parsedRemoteParameterList.filter(o => o.type === "child").map(child => ({
        id: child.data.text,
        parent: child.parent,
        name: child.data.text,
        name_orig: child.text,
        param_no: child.data.param_no,
        desc: child.data.description,
        type: mapType(child.data.type),
        min: parseFloat(child.data.minvalue),
        max: parseFloat(child.data.maxvalue),
        // res: 1, // Not known in new file
        default: child.data.value,
        unit: child.data.unit,
        options: child.data.type === "enum" ?
            child.data.enum.substring(1, child.data.enum.length - 1).split(",\n")
                .map(s => s.trim().split("=").map(e => e.trim()))
                .toMap() : ""
    })).sort((left, right) => {
        if (left.param_no == null && right.param_no == null) return 0
        if (left.param_no == null) return 1
        if (right.param_no == null) return -1
        return left.param_no - right.param_no
    })
    
    parameterNodeList.forEach(o => { // Glue parameter nodes on parents
        const parentNode = IdParentNodeMap[o.parent]
        if (parentNode) parentNode.children.push({
            type: "parameter",
            isVisibleToTechnicalBasic: true, // TODO: how to see if should be visible
            data: o
        })
    })

    console.log("Generated hierarchy from firmware file", parameterNodeList)

    return {
        paramSpec: parameterNodeList.map(p => [p.id, p]).toMap(),
        hierarchyList: [
            ...parentNodeList.filter(o => o.parent === "#"),
            {
                name: "All Parameters",
                type: "category",
                id: "5fa389d5-dcc3-48f0-adc7-c4c15215a2f0",
                isCollapsed: true,
                isVisibleToTechnicalBasic: true,
                children: parameterNodeList.map(p => ({
                    type: "parameter",
                    isVisibleToTechnicalBasic: true, // TODO: how to see if should be visible
                    data: p
                }))
            }
        ]
    }
}

const mapType = (type) => {
    switch (type) {
        case 'float':
            return 'decimal'
        case 'uint32_t':
            return 'int'
        case 'uint16_t':
            return 'int'
        case 'uint8_t':
            return 'int'
        case 'int32_t':
            return 'int'
        case 'int16_t':
            return 'int'
        default:
            return type
    }
}